import type {
  AnalyticsWindowDetail,
  DecisionCheckRow,
  WindowTick,
} from "../api/schemas";

interface DecisionConfig {
  min_odds: number;
  max_odds: number;
  min_matching_candles: number;
  candles_to_check: number;
  decision_time_seconds: number;
  monitor_after_decision: boolean;
  odds_momentum_enabled: boolean;
  odds_momentum_lookback: number;
  odds_momentum_required_increasing: number;
  odds_momentum_ignore_last_seconds: number;
  odds_momentum_use_unique_odds: boolean;
  odds_momentum_avg_lookback: number;
  odds_momentum_avg_max_diff: number;
  // V3 multi-market candle controls. ``candle_timeframe_seconds == 0``
  // means "use the window's ``bucket_seconds``" (zero-impact for legacy
  // windows that pre-date these fields). ``skip_first_candles``
  // discards the lowest-index N candles before ``candles_to_check``
  // is applied. ``allow_live_candle`` / ``live_candle_minute`` mirror
  // the engine.
  candle_timeframe_seconds: number;
  skip_first_candles: number;
  allow_live_candle: boolean;
  live_candle_minute: number;
  // Open-price cushion gate (see trading_bot.decide). Requires current
  // price at least N bps beyond the window open in the favoured
  // direction. Default off so legacy windows reconstruct unchanged.
  open_price_cushion_enabled: boolean;
  open_price_cushion_min_bps: number;
  // Band-dwell gate (see trading_bot.loop). Requires the picked side's
  // mid to have sat inside [min_odds, max_odds] for >= N pre-decision
  // seconds. Default off so legacy windows reconstruct unchanged.
  pre_decision_avg_odds_enabled: boolean;
  pre_decision_avg_odds_min: number;
  band_dwell_enabled: boolean;
  band_dwell_min_seconds: number;
  band_dwell_require_exceeds_opposite: boolean;
  // Flip Count gate (see trading_bot.loop `_flip_observe`). Counts, per
  // side, drops from >= flip threshold to below it since the warm-up skip;
  // the picked side's count must be <= max flips. Default off.
  flip_gate_enabled: boolean;
  flip_gate_max_flips: number;
  flip_gate_skip_seconds: number;
  flip_gate_flip_threshold: number;
  // Favorite Hold (3-tier unflipped-favourite entry). The fallback does
  // NOT reconstruct the tier verdicts (mirroring the counterpart
  // features, which are also engine-only), but the fields are resolved
  // with safe defaults so replayed configs never crash and the values
  // can be surfaced if needed. Default off so legacy windows
  // reconstruct unchanged.
  favorite_hold_enabled: boolean;
  favorite_hold_flip_threshold: number;
  favorite_hold_skip_seconds: number;
  favorite_hold_tier1_enabled: boolean;
  favorite_hold_tier1_min_odds: number;
  favorite_hold_tier1_dwell_seconds: number;
  favorite_hold_tier2_enabled: boolean;
  favorite_hold_tier2_min_rise: number;
  favorite_hold_tier2_check_seconds: number;
  favorite_hold_tier3_enabled: boolean;
}

const DEFAULT_CFG: DecisionConfig = {
  min_odds: 0.65,
  max_odds: 0.95,
  min_matching_candles: 3,
  candles_to_check: 4,
  decision_time_seconds: 234,
  monitor_after_decision: true,
  odds_momentum_enabled: true,
  odds_momentum_lookback: 8,
  odds_momentum_required_increasing: 8,
  odds_momentum_ignore_last_seconds: 3,
  odds_momentum_use_unique_odds: true,
  odds_momentum_avg_lookback: 0,
  odds_momentum_avg_max_diff: 0.5,
  candle_timeframe_seconds: 0,
  skip_first_candles: 0,
  allow_live_candle: false,
  live_candle_minute: 0,
  open_price_cushion_enabled: false,
  open_price_cushion_min_bps: 2,
  pre_decision_avg_odds_enabled: false,
  pre_decision_avg_odds_min: 0.53,
  band_dwell_enabled: false,
  band_dwell_min_seconds: 0,
  band_dwell_require_exceeds_opposite: false,
  flip_gate_enabled: false,
  flip_gate_max_flips: 0,
  flip_gate_skip_seconds: 0,
  flip_gate_flip_threshold: 0.5,
  favorite_hold_enabled: false,
  favorite_hold_flip_threshold: 0.51,
  favorite_hold_skip_seconds: 0,
  favorite_hold_tier1_enabled: false,
  favorite_hold_tier1_min_odds: 0.8,
  favorite_hold_tier1_dwell_seconds: 60,
  favorite_hold_tier2_enabled: false,
  favorite_hold_tier2_min_rise: 0.1,
  favorite_hold_tier2_check_seconds: 120,
  favorite_hold_tier3_enabled: false,
};

function toNum(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function toBool(v: unknown, fallback: boolean): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    if (v.toLowerCase() === "true") return true;
    if (v.toLowerCase() === "false") return false;
  }
  return fallback;
}

export function resolveDecisionConfig(raw: Record<string, unknown> | null | undefined): DecisionConfig {
  const cfg = raw ?? {};
  return {
    min_odds: toNum(cfg.min_odds, DEFAULT_CFG.min_odds),
    max_odds: toNum(cfg.max_odds, DEFAULT_CFG.max_odds),
    min_matching_candles: Math.max(1, Math.floor(toNum(cfg.min_matching_candles, DEFAULT_CFG.min_matching_candles))),
    candles_to_check: Math.max(1, Math.floor(toNum(cfg.candles_to_check, DEFAULT_CFG.candles_to_check))),
    decision_time_seconds: Math.max(0, Math.floor(toNum(cfg.decision_time_seconds, DEFAULT_CFG.decision_time_seconds))),
    monitor_after_decision: toBool(cfg.monitor_after_decision, DEFAULT_CFG.monitor_after_decision),
    odds_momentum_enabled: toBool(cfg.odds_momentum_enabled, DEFAULT_CFG.odds_momentum_enabled),
    odds_momentum_lookback: Math.max(1, Math.floor(toNum(cfg.odds_momentum_lookback, DEFAULT_CFG.odds_momentum_lookback))),
    odds_momentum_required_increasing: Math.max(1, Math.floor(toNum(
      cfg.odds_momentum_required_increasing,
      DEFAULT_CFG.odds_momentum_required_increasing,
    ))),
    odds_momentum_ignore_last_seconds: Math.max(0, Math.floor(toNum(
      cfg.odds_momentum_ignore_last_seconds,
      DEFAULT_CFG.odds_momentum_ignore_last_seconds,
    ))),
    odds_momentum_use_unique_odds: toBool(
      cfg.odds_momentum_use_unique_odds,
      DEFAULT_CFG.odds_momentum_use_unique_odds,
    ),
    odds_momentum_avg_lookback: Math.max(0, Math.floor(toNum(cfg.odds_momentum_avg_lookback, DEFAULT_CFG.odds_momentum_avg_lookback))),
    odds_momentum_avg_max_diff: Math.max(0, toNum(cfg.odds_momentum_avg_max_diff, DEFAULT_CFG.odds_momentum_avg_max_diff)),
    candle_timeframe_seconds: Math.max(0, Math.floor(toNum(cfg.candle_timeframe_seconds, DEFAULT_CFG.candle_timeframe_seconds))),
    skip_first_candles: Math.max(0, Math.floor(toNum(cfg.skip_first_candles, DEFAULT_CFG.skip_first_candles))),
    allow_live_candle: toBool(cfg.allow_live_candle, DEFAULT_CFG.allow_live_candle),
    live_candle_minute: Math.max(0, Math.floor(toNum(cfg.live_candle_minute, DEFAULT_CFG.live_candle_minute))),
    open_price_cushion_enabled: toBool(cfg.open_price_cushion_enabled, DEFAULT_CFG.open_price_cushion_enabled),
    open_price_cushion_min_bps: Math.max(0, toNum(cfg.open_price_cushion_min_bps, DEFAULT_CFG.open_price_cushion_min_bps)),
    pre_decision_avg_odds_enabled: toBool(cfg.pre_decision_avg_odds_enabled, false),
    pre_decision_avg_odds_min: toNum(cfg.pre_decision_avg_odds_min, 0.53),
    band_dwell_enabled: toBool(cfg.band_dwell_enabled, DEFAULT_CFG.band_dwell_enabled),
    band_dwell_min_seconds: Math.max(0, Math.floor(toNum(cfg.band_dwell_min_seconds, DEFAULT_CFG.band_dwell_min_seconds))),
    band_dwell_require_exceeds_opposite: toBool(cfg.band_dwell_require_exceeds_opposite, DEFAULT_CFG.band_dwell_require_exceeds_opposite),
    flip_gate_enabled: toBool(cfg.flip_gate_enabled, DEFAULT_CFG.flip_gate_enabled),
    flip_gate_max_flips: Math.max(0, Math.floor(toNum(cfg.flip_gate_max_flips, DEFAULT_CFG.flip_gate_max_flips))),
    flip_gate_skip_seconds: Math.max(0, Math.floor(toNum(cfg.flip_gate_skip_seconds, DEFAULT_CFG.flip_gate_skip_seconds))),
    flip_gate_flip_threshold: Math.min(1, Math.max(0, toNum(cfg.flip_gate_flip_threshold, DEFAULT_CFG.flip_gate_flip_threshold))),
    favorite_hold_enabled: toBool(cfg.favorite_hold_enabled, DEFAULT_CFG.favorite_hold_enabled),
    favorite_hold_flip_threshold: Math.min(1, Math.max(0, toNum(cfg.favorite_hold_flip_threshold, DEFAULT_CFG.favorite_hold_flip_threshold))),
    favorite_hold_skip_seconds: Math.max(0, Math.floor(toNum(cfg.favorite_hold_skip_seconds, DEFAULT_CFG.favorite_hold_skip_seconds))),
    favorite_hold_tier1_enabled: toBool(cfg.favorite_hold_tier1_enabled, DEFAULT_CFG.favorite_hold_tier1_enabled),
    favorite_hold_tier1_min_odds: Math.min(1, Math.max(0, toNum(cfg.favorite_hold_tier1_min_odds, DEFAULT_CFG.favorite_hold_tier1_min_odds))),
    favorite_hold_tier1_dwell_seconds: Math.max(0, Math.floor(toNum(cfg.favorite_hold_tier1_dwell_seconds, DEFAULT_CFG.favorite_hold_tier1_dwell_seconds))),
    favorite_hold_tier2_enabled: toBool(cfg.favorite_hold_tier2_enabled, DEFAULT_CFG.favorite_hold_tier2_enabled),
    favorite_hold_tier2_min_rise: Math.min(1, Math.max(0, toNum(cfg.favorite_hold_tier2_min_rise, DEFAULT_CFG.favorite_hold_tier2_min_rise))),
    favorite_hold_tier2_check_seconds: Math.max(0, Math.floor(toNum(cfg.favorite_hold_tier2_check_seconds, DEFAULT_CFG.favorite_hold_tier2_check_seconds))),
    favorite_hold_tier3_enabled: toBool(cfg.favorite_hold_tier3_enabled, DEFAULT_CFG.favorite_hold_tier3_enabled),
  };
}

function tickMs(ts: string): number {
  return new Date(ts).getTime();
}

function offsetSec(ts: string, windowStart: string): number {
  return Math.max(0, Math.floor((tickMs(ts) - tickMs(windowStart)) / 1000));
}

function candleAgreement(
  ticks: WindowTick[],
  direction: "UP" | "DOWN",
  candlesToCheck: number,
  windowStart: string,
  opts?: {
    /** Native bucket width for the window's market (default 60s). */
    bucketSeconds?: number;
    /** Operator override (0 = use ``bucketSeconds``). */
    candleTimeframeSeconds?: number;
    /** Drop the lowest-index N qualifying candles. */
    skipFirstCandles?: number;
    /** Match the engine's live-candle semantics. */
    allowLiveCandle?: boolean;
    liveCandleMinute?: number;
  },
): { matching: number; evaluated: number } {
  if (ticks.length === 0) return { matching: 0, evaluated: 0 };
  const tfOverride = Math.max(0, Math.floor(opts?.candleTimeframeSeconds ?? 0));
  const nativeBucket = Math.max(1, Math.floor(opts?.bucketSeconds ?? 60));
  const bucketSec = tfOverride > 0 ? tfOverride : nativeBucket;
  const skipFirst = Math.max(0, Math.floor(opts?.skipFirstCandles ?? 0));
  const allowLive = Boolean(opts?.allowLiveCandle);
  const liveMinute = Math.max(0, Math.floor(opts?.liveCandleMinute ?? 0));
  const byMinute = new Map<number, number[]>();
  for (const t of ticks) {
    const m = Math.floor(offsetSec(t.ts, windowStart) / bucketSec) + 1;
    if (m <= 0) continue;
    const p = Number(t.btc_price);
    if (!Number.isFinite(p)) continue;
    if (!byMinute.has(m)) byMinute.set(m, []);
    byMinute.get(m)!.push(p);
  }
  const qualifying = Array.from(byMinute.keys())
    .sort((a, b) => a - b)
    .filter((m) => byMinute.has(m + 1));
  // V3 multi-market: live candle eligibility (mirrors the engine).
  if (allowLive && byMinute.size > 0) {
    const currentMinute = Math.max(...byMinute.keys());
    if (!qualifying.includes(currentMinute)) {
      const allowedForMinute = liveMinute === 0 || currentMinute >= liveMinute;
      if (allowedForMinute && (byMinute.get(currentMinute)?.length ?? 0) >= 2) {
        qualifying.push(currentMinute);
      }
    }
  }
  // V3 multi-market: drop the first N candle indices (closed + live).
  const eligible = skipFirst > 0
    ? qualifying.filter((m) => m > skipFirst)
    : qualifying;
  if (eligible.length === 0) return { matching: 0, evaluated: 0 };
  const recent = eligible.slice(-candlesToCheck);
  let matching = 0;
  for (const m of recent) {
    const prices = byMinute.get(m) ?? [];
    if (prices.length < 2) continue;
    const delta = prices[prices.length - 1]! - prices[0]!;
    if (direction === "UP" && delta > 0) matching += 1;
    if (direction === "DOWN" && delta < 0) matching += 1;
  }
  return { matching, evaluated: recent.length };
}

function favoriteForTick(t: WindowTick): { direction: "UP" | "DOWN"; mid: number } | null {
  const upBid = t.up_bid == null ? null : Number(t.up_bid);
  const upAsk = t.up_ask == null ? null : Number(t.up_ask);
  const dnBid = t.down_bid == null ? null : Number(t.down_bid);
  const dnAsk = t.down_ask == null ? null : Number(t.down_ask);
  const upMid = upBid != null && upAsk != null && Number.isFinite(upBid) && Number.isFinite(upAsk)
    ? (upBid + upAsk) / 2
    : null;
  const dnMid = dnBid != null && dnAsk != null && Number.isFinite(dnBid) && Number.isFinite(dnAsk)
    ? (dnBid + dnAsk) / 2
    : null;
  if (upMid == null && dnMid == null) return null;
  if (upMid != null && (dnMid == null || upMid >= dnMid)) {
    return { direction: "UP", mid: upMid };
  }
  return { direction: "DOWN", mid: dnMid! };
}

/**
 * Mirrors `trading_bot.loop.TickLoop._implied_odds`: each side's value is
 * the bid/ask midpoint, with `1 - other_mid` used as a fallback when only
 * the other side is quoted. The momentum gate in the bot is evaluated on
 * these mid values (see `_record_odds_history` + `_momentum_pass`), so
 * the frontend must use the same quantity to reconstruct it faithfully.
 */
export function sideOdds(t: WindowTick, direction: "UP" | "DOWN"): number | null {
  const parseProb = (v: string | null | undefined): number | null => {
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const upBid = parseProb(t.up_bid);
  const upAsk = parseProb(t.up_ask);
  const dnBid = parseProb(t.down_bid);
  const dnAsk = parseProb(t.down_ask);
  let upMid = upBid != null && upAsk != null ? (upBid + upAsk) / 2 : null;
  let dnMid = dnBid != null && dnAsk != null ? (dnBid + dnAsk) / 2 : null;
  if (upMid != null && dnMid == null) dnMid = 1 - upMid;
  if (dnMid != null && upMid == null) upMid = 1 - dnMid;
  return direction === "UP" ? upMid : dnMid;
}

function preDecisionAverages(ticks: WindowTick[], startMs: number, cutoff: number): [number | null, number | null] {
  const seconds = new Map<number, WindowTick>();
  for (const tick of ticks) {
    const elapsed = (tickMs(tick.ts) - startMs) / 1000;
    if (elapsed >= cutoff) break;
    if (Number.isFinite(elapsed) && elapsed >= 0) seconds.set(Math.floor(elapsed), tick);
  }
  const sums = [0, 0], counts = [0, 0];
  for (const tick of seconds.values()) {
    for (const [i, pair] of [[tick.up_bid, tick.up_ask], [tick.down_bid, tick.down_ask]].entries()) {
      const [bid, ask] = pair;
      if (bid == null || ask == null || bid.trim() === "" || ask.trim() === "") continue;
      const b = Number(bid), a = Number(ask);
      if (b >= 0 && b <= 1 && a >= 0 && a <= 1) {
        sums[i]! += (b + a) / 2;
        counts[i]! += 1;
      }
    }
  }
  return [counts[0]! ? sums[0]! / counts[0]! : null, counts[1]! ? sums[1]! / counts[1]! : null];
}

interface MomentumSummary {
  passed: boolean | null;
  ratio: string;
  checkedOdds: string[];
  detail: string;
  // Canonical fields (STRATEGY_SPEC.md §4). `current` / `prior` are the
  // exact numeric values used in the gate; `observed` / `increasing` /
  // `lookback` / `required` make the per-tick verdict reproducible.
  observed: number;
  increasing: number;
  lookback: number;
  required: number;
  current: number | null;
  prior: number[];
  bypassed: boolean;
  avgLookback: number;
  avgMaxDiff: number;
  avgDiff: number | null;
  avgPrior: number[];
  avgPassed: boolean | null;
}

/**
 * Canonical momentum reconstruction for the UI fallback (legacy windows
 * with no persisted `odds_momentum` check). Matches
 * `V2/STRATEGY_SPEC.md` §4 exactly:
 *
 *   - window-scoped odds history (unique-or-recorded per config)
 *   - `current` is the actual current tick's mid, NOT history's last
 *   - fail-closed when fewer than `lookback` prior values exist
 */
export function momentumSnapshot(
  ticks: WindowTick[],
  direction: "UP" | "DOWN",
  secsLeft: number,
  cfg: DecisionConfig,
): MomentumSummary {
  const useUniqueOdds = cfg.odds_momentum_use_unique_odds;

  // Build window-scoped history in chronological order. When
  // `odds_momentum_use_unique_odds` is on, keep first appearance only;
  // otherwise keep all recorded values (including duplicates).
  const history: number[] = [];
  let lastTickMid: number | null = null;
  for (const t of ticks) {
    const v = sideOdds(t, direction);
    if (v == null || !Number.isFinite(v)) continue;
    lastTickMid = v;
    if (useUniqueOdds) {
      if (!history.some((existing) => existing === v)) history.push(v);
      continue;
    }
    history.push(v);
  }
  return momentumFromState(history, lastTickMid, secsLeft, cfg);
}

/**
 * Same result as `momentumSnapshot`, but the caller supplies the pre-built
 * per-direction history and the current tick's mid. Used by the Analytics
 * enrichment path so we can walk ticks once and evaluate momentum for
 * every check row in O(T + N) instead of O(N * T).
 */
export function momentumFromState(
  history: number[],
  lastTickMid: number | null,
  secsLeft: number,
  cfg: DecisionConfig,
): MomentumSummary {
  const lookback = Math.max(1, Math.floor(cfg.odds_momentum_lookback));
  const required = Math.max(0, Math.floor(cfg.odds_momentum_required_increasing));
  const useUniqueOdds = cfg.odds_momentum_use_unique_odds;
  const avgLookback = Math.max(0, Math.floor(cfg.odds_momentum_avg_lookback ?? 0));
  const avgMaxDiff = Math.max(0, cfg.odds_momentum_avg_max_diff ?? 0);
  const base = {
    lookback,
    required,
    bypassed: false,
    observed: 0,
    increasing: 0,
    current: lastTickMid,
    prior: [] as number[],
    avgLookback,
    avgMaxDiff,
    avgDiff: null as number | null,
    avgPrior: [] as number[],
    avgPassed: null as boolean | null,
  };

  if (!cfg.odds_momentum_enabled) {
    return { ...base, passed: null, ratio: "—", checkedOdds: [], detail: "momentum disabled" };
  }
  if (cfg.odds_momentum_ignore_last_seconds > 0 && secsLeft <= cfg.odds_momentum_ignore_last_seconds) {
    return {
      ...base,
      passed: true,
      bypassed: true,
      ratio: "bypassed",
      checkedOdds: history.slice(-(lookback + 1)).map((n) => n.toFixed(3)),
      detail: "momentum bypassed near window end",
    };
  }
  if (lastTickMid == null) {
    return {
      ...base,
      passed: false,
      ratio: `0/${required}`,
      checkedOdds: [],
      detail: "no current odds for direction",
    };
  }
  const current = lastTickMid;
  const priorCandidates = useUniqueOdds
    ? history.filter((v) => v !== current)
    : (() => {
      const raw = history.slice();
      for (let i = raw.length - 1; i >= 0; i -= 1) {
        if (raw[i] === current) {
          raw.splice(i, 1);
          break;
        }
      }
      return raw;
    })();
  // `lookback` is a CEILING on how many of the most-recent priors we
  // inspect, NOT a floor: early in the window we evaluate against
  // whatever priors exist so far. The gate passes as soon as `required`
  // of the inspected priors are below current; warm-up still fails
  // naturally because `below` cannot reach `required` until enough
  // priors exist. See STRATEGY_SPEC.md §4.
  const prior = priorCandidates.slice(-lookback);
  const observed = prior.length;
  const below = prior.filter((v) => v < current).length;
  const primaryPassed = below >= required;
  let avgDiff: number | null = null;
  let avgPrior: number[] = [];
  let avgPassed: boolean | null = null;
  let passed = primaryPassed;
  if (primaryPassed && avgLookback > 0 && avgMaxDiff > 0) {
    const avgPriorCandidates = priorCandidates.slice(-avgLookback);
    if (avgPriorCandidates.length > 0) {
      const avgVal = avgPriorCandidates.reduce((s, v) => s + v, 0) / avgPriorCandidates.length;
      avgDiff = current - avgVal;
      avgPrior = avgPriorCandidates;
      avgPassed = avgDiff <= avgMaxDiff;
      if (!avgPassed) passed = false;
    }
  }
  let detail = `${below} of ${observed} prior ${useUniqueOdds ? "distinct " : ""}odds < current`;
  if (avgPassed === false && avgDiff != null) {
    detail += `; avg diff ${avgDiff.toFixed(4)} > max ${avgMaxDiff.toFixed(4)} (spike detected)`;
  }
  return {
    ...base,
    passed,
    observed,
    increasing: below,
    prior,
    avgDiff,
    avgPrior,
    avgPassed,
    ratio: `${below}/${required}`,
    checkedOdds: [...prior, current].map((n) => n.toFixed(3)),
    detail,
  };
}

/**
 * Compute the matching/evaluated candle counts from a pre-populated
 * ``byMinute`` map. Used by the streaming ``synthesizeDecisionChecks``
 * so we don't rebuild the map on every tick (which was O(T²) and froze
 * the Market Data page on 1h/4h windows).
 */
function candleAgreementFromMap(
  byMinute: Map<number, number[]>,
  direction: "UP" | "DOWN",
  candlesToCheck: number,
  opts: {
    skipFirstCandles?: number;
    allowLiveCandle?: boolean;
    liveCandleMinute?: number;
  } = {},
): { matching: number; evaluated: number } {
  if (byMinute.size === 0) return { matching: 0, evaluated: 0 };
  const skipFirst = Math.max(0, Math.floor(opts.skipFirstCandles ?? 0));
  const allowLive = Boolean(opts.allowLiveCandle);
  const liveMinute = Math.max(0, Math.floor(opts.liveCandleMinute ?? 0));
  const qualifying = Array.from(byMinute.keys())
    .sort((a, b) => a - b)
    .filter((m) => byMinute.has(m + 1));
  if (allowLive) {
    const currentMinute = Math.max(...byMinute.keys());
    if (!qualifying.includes(currentMinute)) {
      const allowedForMinute = liveMinute === 0 || currentMinute >= liveMinute;
      if (allowedForMinute && (byMinute.get(currentMinute)?.length ?? 0) >= 2) {
        qualifying.push(currentMinute);
      }
    }
  }
  const eligible = skipFirst > 0
    ? qualifying.filter((m) => m > skipFirst)
    : qualifying;
  if (eligible.length === 0) return { matching: 0, evaluated: 0 };
  const recent = eligible.slice(-candlesToCheck);
  let matching = 0;
  for (const m of recent) {
    const prices = byMinute.get(m) ?? [];
    if (prices.length < 2) continue;
    const delta = prices[prices.length - 1]! - prices[0]!;
    if (direction === "UP" && delta > 0) matching += 1;
    if (direction === "DOWN" && delta < 0) matching += 1;
  }
  return { matching, evaluated: recent.length };
}

export function synthesizeDecisionChecks(
  detail: AnalyticsWindowDetail,
  ticksIn: WindowTick[],
  activeConfig?: Record<string, unknown> | null,
  options?: { simulatorMode?: boolean; fullTrace?: boolean },
): DecisionCheckRow[] {
  if (ticksIn.length === 0) return [];
  const cfg = resolveDecisionConfig(activeConfig);
  // Perf: parse each tick's timestamp exactly once. Sorting + downstream
  // logic previously called ``new Date(ts).getTime()`` on the order of
  // T² times per window, which for 4h (T≈14400) is ~10^8 Date.parse
  // calls and locks the main thread for tens of seconds.
  const ticks = [...ticksIn].sort((a, b) => tickMs(a.ts) - tickMs(b.ts));
  const tickMsArr: number[] = new Array(ticks.length);
  for (let i = 0; i < ticks.length; i += 1) tickMsArr[i] = tickMs(ticks[i]!.ts);

  const simulatorMode = Boolean(options?.simulatorMode);
  // ``fullTrace`` bypasses the ``decision_time_seconds`` warm-up cutoff
  // AND the ORDER PLACED cutoff so the operator can inspect what the
  // decision engine would have said at every recorded tick across the
  // whole window. Used by the Analytics page's "Simulate checks"
  // button so the per-minute feed populates every second.
  const fullTrace = Boolean(options?.fullTrace);
  const execMs = !simulatorMode && !fullTrace && detail.bet ? tickMs(detail.bet.created_at) : null;
  const windowStartMsVal = tickMs(detail.window_start);
  const frozenAverages = cfg.pre_decision_avg_odds_enabled
    ? preDecisionAverages(ticks, windowStartMsVal, cfg.decision_time_seconds)
    : [null, null];
  const bucketSec = cfg.candle_timeframe_seconds > 0
    ? cfg.candle_timeframe_seconds
    : Math.max(1, Math.floor(detail.bucket_seconds ?? 60));

  const rows: DecisionCheckRow[] = [];
  // Incremental state — the previous implementation rebuilt these on
  // every tick which is what caused the O(T²) blowup.
  const byMinute = new Map<number, number[]>();
  const useUnique = cfg.odds_momentum_use_unique_odds;
  const historyUp: number[] = [];
  const historyDn: number[] = [];
  const seenUp: Set<number> | null = useUnique ? new Set() : null;
  const seenDn: Set<number> | null = useUnique ? new Set() : null;
  let lastMidUp: number | null = null;
  let lastMidDn: number | null = null;
  // Band-dwell counters: pre-decision seconds each side's mid sat inside
  // [min_odds, max_odds]. Ticks arrive on the persisted 1Hz grid, so one
  // row == one second. Mirrors trading_bot.loop / the sim engines.
  let dwellUp = 0;
  let dwellDn = 0;
  // Flip Count gate state: per-side flip counters + previous "at/above
  // threshold" flag, seeded at the first tick after the warm-up skip.
  // Mirrors trading_bot.loop `_flip_observe` / the sim engines.
  let flipsUp = 0;
  let flipsDn = 0;
  let flipWasFavUp: boolean | null = null;
  let flipWasFavDn: boolean | null = null;
  // Track the first synthetic "place" so we can stop simulating after the
  // simulator's own bet decision (and avoid producing a noisy long trace).
  let simPlacedAt: number | null = null;
  // Window open = first finite BTC sample (mirrors trading_bot.btc_open()).
  // Used by the open-price cushion gate reconstruction below.
  let windowOpenPx: number | null = null;
  for (const tk of ticks) {
    const p0 = Number(tk.btc_price);
    if (Number.isFinite(p0)) { windowOpenPx = p0; break; }
  }

  for (let i = 0; i < ticks.length; i += 1) {
    const t = ticks[i]!;
    const tms = tickMsArr[i]!;
    // Update candle map (byMinute) incrementally.
    const priceOff = Math.max(0, Math.floor((tms - windowStartMsVal) / 1000));
    const minuteIdx = Math.floor(priceOff / bucketSec) + 1;
    if (minuteIdx > 0) {
      const p = Number(t.btc_price);
      if (Number.isFinite(p)) {
        let arr = byMinute.get(minuteIdx);
        if (!arr) { arr = []; byMinute.set(minuteIdx, arr); }
        arr.push(p);
      }
    }
    // Update per-direction momentum histories incrementally.
    const upV = sideOdds(t, "UP");
    const dnV = sideOdds(t, "DOWN");
    if (upV != null && Number.isFinite(upV)) {
      lastMidUp = upV;
      if (seenUp) { if (!seenUp.has(upV)) { seenUp.add(upV); historyUp.push(upV); } }
      else historyUp.push(upV);
    }
    if (dnV != null && Number.isFinite(dnV)) {
      lastMidDn = dnV;
      if (seenDn) { if (!seenDn.has(dnV)) { seenDn.add(dnV); historyDn.push(dnV); } }
      else historyDn.push(dnV);
    }
    // Band-dwell accumulation (pre-decision only).
    if (cfg.band_dwell_enabled && priceOff < cfg.decision_time_seconds) {
      if (upV != null && upV >= cfg.min_odds && upV <= cfg.max_odds) dwellUp += 1;
      if (dnV != null && dnV >= cfg.min_odds && dnV <= cfg.max_odds) dwellDn += 1;
    }
    // Flip Count accumulation (warm-up ticks skipped entirely).
    if (cfg.flip_gate_enabled && (tms - windowStartMsVal) / 1000 >= cfg.flip_gate_skip_seconds) {
      if (upV != null && Number.isFinite(upV)) {
        const isFav = upV >= cfg.flip_gate_flip_threshold;
        if (flipWasFavUp === true && !isFav) flipsUp += 1;
        flipWasFavUp = isFav;
      }
      if (dnV != null && Number.isFinite(dnV)) {
        const isFav = dnV >= cfg.flip_gate_flip_threshold;
        if (flipWasFavDn === true && !isFav) flipsDn += 1;
        flipWasFavDn = isFav;
      }
    }

    const off = priceOff;
    // ``fullTrace`` mode ignores warm-up + exec cutoffs so the operator
    // sees a row per recorded tick across the whole window.
    if (!fullTrace && off < cfg.decision_time_seconds) continue;
    if (execMs != null && tms > execMs) break;
    if (simulatorMode && simPlacedAt != null && tms > simPlacedAt) break;

    const fav = favoriteForTick(t);
    if (!fav) {
      rows.push({
        check_ts: t.ts,
        outcome: "blocked",
        direction: null,
        favorite_odds: null,
        matching_candles: null,
        candles_evaluated: null,
        block_reason: "empty orderbook",
        checks: [],
        config_version_id: detail.config_version_id,
      });
      continue;
    }

    const { matching, evaluated } = candleAgreementFromMap(
      byMinute,
      fav.direction,
      cfg.candles_to_check,
      {
        skipFirstCandles: cfg.skip_first_candles,
        allowLiveCandle: cfg.allow_live_candle,
        liveCandleMinute: cfg.live_candle_minute,
      },
    );
    const history = fav.direction === "UP" ? historyUp : historyDn;
    const lastMid = fav.direction === "UP" ? lastMidUp : lastMidDn;
    const momentum = momentumFromState(history, lastMid, t.secs_left, cfg);
    const averageReady = off >= cfg.decision_time_seconds;
    const frozenAverage = averageReady ? frozenAverages[fav.direction === "UP" ? 0 : 1] : null;
    const averagePassed = frozenAverage != null && Number.isFinite(frozenAverage)
      && cfg.pre_decision_avg_odds_min >= 0 && cfg.pre_decision_avg_odds_min <= 1
      && Number(frozenAverage.toFixed(12)) >= cfg.pre_decision_avg_odds_min;
    const inBand = fav.mid >= cfg.min_odds && fav.mid <= cfg.max_odds;
    const enough = matching >= cfg.min_matching_candles;
    // Open-price cushion: current price at least ``min_bps`` bps beyond the
    // window open in the favoured direction (mirrors trading_bot.decide).
    const curPx = Number(t.btc_price);
    const cushionBps = (windowOpenPx != null && Number.isFinite(curPx) && windowOpenPx !== 0)
      ? ((fav.direction === "UP" ? curPx - windowOpenPx : windowOpenPx - curPx) / windowOpenPx) * 10000
      : null;
    const openCushionOk = cushionBps != null && cushionBps >= cfg.open_price_cushion_min_bps;

    const dwellObserved = fav.direction === "UP" ? dwellUp : dwellDn;
    const dwellOpposite = fav.direction === "UP" ? dwellDn : dwellUp;
    const bandDwellActive = cfg.band_dwell_enabled
      && (cfg.band_dwell_min_seconds > 0 || cfg.band_dwell_require_exceeds_opposite);
    const bandDwellOk = (cfg.band_dwell_min_seconds <= 0 || dwellObserved >= cfg.band_dwell_min_seconds)
      && (!cfg.band_dwell_require_exceeds_opposite || dwellObserved > dwellOpposite);
    const flipsObserved = fav.direction === "UP" ? flipsUp : flipsDn;
    const flipGateOk = flipsObserved <= cfg.flip_gate_max_flips;

    let outcome: DecisionCheckRow["outcome"] = "place";
    let blockReason: string | null = null;
    if (!inBand) {
      outcome = "blocked";
      blockReason = "favorite odds outside configured band";
    } else if (cfg.open_price_cushion_enabled && !openCushionOk) {
      outcome = "blocked";
      blockReason = cushionBps == null
        ? "open-price cushion gate: no price sample yet (cold start)"
        : "price within minimum cushion of window open";
    } else if (!enough) {
      outcome = cfg.monitor_after_decision ? "monitor" : "skip";
      blockReason = "not enough candle agreement";
    } else if (momentum.passed === false) {
      outcome = "blocked";
      blockReason = "momentum";
    } else if (cfg.pre_decision_avg_odds_enabled && !averagePassed) {
      outcome = cfg.monitor_after_decision ? "monitor" : "skip";
      blockReason = "pre-decision average odds gate failed";
    } else if (bandDwellActive && !bandDwellOk) {
      outcome = cfg.monitor_after_decision ? "monitor" : "skip";
      blockReason = "band dwell below minimum";
    } else if (cfg.flip_gate_enabled && !flipGateOk) {
      outcome = "skip";
      blockReason = "too many favorite flips";
    }

    if (simulatorMode && outcome === "place") {
      simPlacedAt = tickMs(t.ts);
    }
    rows.push({
      check_ts: t.ts,
      outcome,
      direction: fav.direction,
      favorite_odds: fav.mid.toFixed(4),
      matching_candles: matching,
      candles_evaluated: evaluated,
      block_reason: blockReason,
      checks: [
        // Open-price cushion gate (only when enabled), mirroring the live
        // bot / data-api sim engine so the Market Data feed shows it.
        ...(cfg.open_price_cushion_enabled ? [{
          name: "open_price_cushion",
          passed: openCushionOk,
          value: {
            open: windowOpenPx,
            current: Number.isFinite(curPx) ? curPx : null,
            cushion_bps: cushionBps == null ? null : Number(cushionBps.toFixed(3)),
            direction: fav.direction,
          },
          threshold: { min_bps: cfg.open_price_cushion_min_bps },
          detail: cushionBps == null
            ? "no price sample yet for window (cold start)"
            : `cushion ${cushionBps.toFixed(2)} bps vs min ${cfg.open_price_cushion_min_bps.toFixed(2)} bps for ${fav.direction}`,
        }] : []),
        // Canonical persisted shape, matches `STRATEGY_SPEC.md` §4 and
        // the live trading bot / data-api sim engine.
        {
          name: "odds_momentum",
          passed: momentum.bypassed ? true : momentum.passed,
          value: {
            observed: momentum.observed,
            increasing: momentum.increasing,
            current: momentum.current,
            prior: momentum.prior,
            avg_diff: momentum.avgDiff,
            avg_prior: momentum.avgPrior,
            avg_passed: momentum.avgPassed,
          },
          threshold: {
            lookback: momentum.lookback,
            required_increasing: momentum.required,
            avg_lookback: momentum.avgLookback,
            avg_max_diff: momentum.avgMaxDiff,
          },
          detail: momentum.detail,
        },
        ...(cfg.pre_decision_avg_odds_enabled ? [{
          name: "pre_decision_avg_odds",
          passed: averagePassed,
          value: {
            up: averageReady ? frozenAverages[0] : null,
            down: averageReady ? frozenAverages[1] : null,
            selected: frozenAverage,
          },
          threshold: { min: cfg.pre_decision_avg_odds_min, decision_time_seconds: cfg.decision_time_seconds },
          detail: averageReady ? "average frozen strictly before configured decision time" : "waiting for configured decision time",
        }] : []),
        // Band-dwell gate (only when enabled), mirroring the live bot /
        // sim engines so the Market Data feed shows it.
        ...(bandDwellActive ? [{
          name: "band_dwell",
          passed: bandDwellOk,
          value: {
            dwell_seconds: dwellObserved,
            opposite_dwell_seconds: dwellOpposite,
          },
          threshold: {
            min_seconds: cfg.band_dwell_min_seconds,
            require_exceeds_opposite: cfg.band_dwell_require_exceeds_opposite,
            band_min: cfg.min_odds,
            band_max: cfg.max_odds,
          },
          detail: `picked side sat ${dwellObserved}s (opposite ${dwellOpposite}s) inside odds band before decision time`,
        }] : []),
        // Flip Count gate (only when enabled), mirroring the live bot /
        // sim engines so the Market Data feed shows it.
        ...(cfg.flip_gate_enabled ? [{
          name: "flip_count",
          passed: flipGateOk,
          value: { flips: flipsObserved, direction: fav.direction },
          threshold: {
            max_flips: cfg.flip_gate_max_flips,
            skip_seconds: cfg.flip_gate_skip_seconds,
            flip_threshold: cfg.flip_gate_flip_threshold,
          },
          detail: `${fav.direction} dropped below ${cfg.flip_gate_flip_threshold.toFixed(2)} ${flipsObserved} time(s) since ${cfg.flip_gate_skip_seconds}s (max ${cfg.flip_gate_max_flips})`,
        }] : []),
      ],
      config_version_id: detail.config_version_id,
    });
  }

  // simulatorMode: never inject the historical bet. The trace is purely
  // a function of the current config, as required by the Manual Simulator.
  if (simulatorMode) return rows;
  if (!detail.bet || execMs == null) return rows;
  if (detail.bet.bet_status === "no_fill") return rows;
  const execTs = detail.bet.created_at;
  const atExecIdx = rows.findIndex((r) => r.check_ts === execTs);
  const prior = atExecIdx >= 0
    ? rows[atExecIdx]!
    : [...rows]
      .filter((r) => tickMs(r.check_ts) <= execMs)
      .sort((a, b) => tickMs(a.check_ts) - tickMs(b.check_ts))
      .at(-1) ?? null;
  const execRow: DecisionCheckRow = {
    check_ts: execTs,
    outcome: "place",
    direction: detail.bet.direction,
    favorite_odds: detail.bet.price,
    matching_candles: prior?.matching_candles ?? null,
    candles_evaluated: prior?.candles_evaluated ?? null,
    block_reason: "order placed and executed",
    checks: prior?.checks ?? [],
    config_version_id: detail.config_version_id,
  };

  if (atExecIdx >= 0) {
    rows[atExecIdx] = execRow;
  } else {
    rows.push(execRow);
  }
  return rows.sort((a, b) => tickMs(a.check_ts) - tickMs(b.check_ts));
}
